// @ts-strict-ignore
import {animate, sequence, style, transition, trigger} from "@angular/animations";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {Location} from "@angular/common";
import {Component, Input, OnInit, output} from "@angular/core";
import {FormControl} from "@angular/forms";
import {Router} from "@angular/router";
import {Media, MediaType, Modules} from "@models";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {SimConfigurationService, SimMaterialDialogService, SimMediasetService, NotificationService} from "@services";
import {Translations} from "@translations";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {debounceTime} from "rxjs/operators";
import {TrackByMethods} from "../../utilities/track-by-methods";

@UntilDestroy()
@Component({
	selector: "sim-basic-administration-media-library-edit-page",
	templateUrl: "./basic-administration-media-library-edit-page.component.html",
	styleUrls: ["./basic-administration-media-library-edit-page.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 250}),
		zoomOutOnLeaveAnimation({duration: 250}),
		trigger("anim", [
			transition("* => void", [
				style({
					height: "*",
					opacity: "1",
					transform: "translateX(0)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".1s ease",
						style({
							height: "0",
							opacity: 0,
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
			transition("void => active", [
				style({
					height: "*",
					opacity: "0",
					transform: "translateX(30px)",
					"box-shadow": "none",
				}),
				sequence([
					animate(
						".1s ease",
						style({
							height: "*",
							opacity: ".2",
							transform: "translateX(30px)",
							"box-shadow": "none",
						}),
					),
					animate(
						".25s ease",
						style({
							height: "*",
							opacity: 1,
							transform: "translateX(0)",
							"box-shadow": "none",
						}),
					),
				]),
			]),
		]),
	],
	standalone: false,
})
export class BasicAdministrationMediaLibraryEditPageComponent implements OnInit {
	T = Translations;
	TrackByMethods = TrackByMethods;

	@Input() loadingData: boolean;
	@Input() data: Media[];

	@Input() loadingAvailableData: boolean;
	@Input() availableData: {[key in keyof typeof MediaType]: Media[]};
	@Input() currentPagesPerMediaType: Map<MediaType, {page: number; end: boolean}> = new Map<MediaType, {page: number; end: boolean}>();

	@Input() title: string;
	@Input() icon: {
		fontSet: "fad" | "fa" | "fas" | "far" | "fab";
		fontIcon: string;
	};

	searching: boolean;
	searchInputControl = new FormControl("");

	@Input() mediaTypes: MediaType[] = [MediaType.Audio, MediaType.Video, MediaType.Image, MediaType.PowerPoint, MediaType.PDF];
	MediaType = MediaType;

	uploadClickedEmitter = output<MediaType>();
	downloadClickedEmitter = output<void>();
	mediaDroppedSelectedEmitter = output<CdkDragDrop<Media[]>>();
	removeMediaFromSelectedEmitter = output<{
		item: Media;
		index: number;
	}>();
	mediaClickedEmitter = output<Media>();
	mediaDroppedAvailableEmitter = output<CdkDragDrop<Media[]>>();
	addMediaToSelectedEmitter = output<{
		item: Media;
		index: number;
	}>();
	editMediaEmitter = output<Media>();
	searchValueChangedEmitter = output<string>();

	deleteMediaEmitter = output<Media>();
	reloadData = output<void>();
	selectedTabIndex: number = 0;
	availableMediaDropListIds: string[];

	constructor(
		private _location: Location,
		private _router: Router,
		private _simMaterialDialogService: SimMaterialDialogService,
		private _simMediaSetService: SimMediasetService,
		private simNotificationService: NotificationService,
	) {
		this.availableMediaDropListIds = this.mediaTypes.map((val) => "availableMedias" + val);

		this.searchInputControl.valueChanges
			.pipe(untilDestroyed(this), debounceTime(500))
			.subscribe((val) => this.searchValueChangedEmitter.emit(val));
	}

	ngOnInit(): void {}

	historyBack() {
		if (SimConfigurationService.hasLicenseModule(Modules.ScenarioDesigner)) {
			this._location.back();
		} else {
			this._router.navigate([""]);
		}
	}

	deleteMedia($event: Media) {
		this._simMaterialDialogService
			.openDeleteMediaDialog({
				mediaId: $event.id,
				mediaName: $event.name,
			})
			.then((dialogRes) => {
				if (dialogRes.action === "ok") {
					this._simMediaSetService
						.removeMediaAndAllLinks({
							mediaId: $event.id,
						})
						.then(() => {
							this.reloadData.emit();
							this.simNotificationService.notifySuccess();
						});
				}
			});
	}

	asMedias(data: any[]) {
		return <Media[]>data;
	}
}
